import type {
  EstimateOpenPosition,
  EstimateVesselReservation,
} from '@seaber/common/core/scenario';
import type {
  LocationId,
  OpenPositionsFragment,
  RegionId,
  VesselReservationFragment,
} from '@seaber/common/graphql/types';
import type { ScenarioLocation } from '../../../lib/planningData';

/** Extract simplified vessel reservation data from a full fragment for storage in EstimateTimeline */
export const toEstimateVesselReservation = (
  vr: VesselReservationFragment,
): EstimateVesselReservation => ({
  id: vr.id,
  duration: vr.duration,
  endPortId: vr.endPortId,
  endRegionId: vr.endRegionId,
  expireTime: vr.expireTime,
  remark: vr.remark,
  startPortId: vr.startPortId,
  startRegionId: vr.startRegionId,
  startTime: vr.startTime,
  type: vr.type,
  vesselId: vr.vesselId,
  userByCreatedByUserId: vr.userByCreatedByUserId
    ? { id: vr.userByCreatedByUserId.id, name: vr.userByCreatedByUserId.name }
    : null,
});

/** Extract simplified open position data from a full fragment for storage in EstimateTimeline */
export const toEstimateOpenPosition = (
  op: OpenPositionsFragment,
): EstimateOpenPosition => ({
  id: op.id,
  directionId: op.directionId,
  remark: op.remark,
  startPortId: op.startPortId,
  startTime: op.startTime,
  vesselId: op.vesselId,
});

type FragmentReconstructionMaps = {
  locationsById?: Map<LocationId, ScenarioLocation>;
  regionsById?: Map<RegionId, { id: RegionId; name: string }>;
  vesselReservationsById?: Map<
    VesselReservationFragment['id'],
    VesselReservationFragment
  >;
};

/** Create helpers to reconstruct full fragments from simplified estimate data */
export function createFragmentReconstructors({
  locationsById,
  regionsById,
  vesselReservationsById,
}: FragmentReconstructionMaps) {
  const getLocationForFragment = (
    locationId: LocationId | null | undefined,
  ): { __typename: 'Location'; id: LocationId; name: string } | null => {
    if (!locationId) return null;
    const loc = locationsById?.get(locationId);
    if (!loc) return null;
    return { __typename: 'Location', id: loc.id, name: loc.name };
  };

  const getRegionForFragment = (
    regionId: RegionId | null | undefined,
  ): {
    __typename: 'Region';
    id: RegionId;
    name: string;
    ordinal?: number | null;
  } | null => {
    if (!regionId) return null;
    const reg = regionsById?.get(regionId);
    if (!reg) return null;
    return { __typename: 'Region', id: reg.id, name: reg.name, ordinal: null };
  };

  /** Reconstruct full OpenPositionsFragment from simplified EstimateOpenPosition */
  const toOpenPositionsFragment = (
    simplified: EstimateOpenPosition,
  ): OpenPositionsFragment => ({
    __typename: 'OpenPosition',
    ...simplified,
    locationByStartPortId: getLocationForFragment(simplified.startPortId),
    locationByDirectionId: getLocationForFragment(simplified.directionId),
  });

  /** Reconstruct full VesselReservationFragment from simplified EstimateVesselReservation */
  const toVesselReservationFragment = (
    simplified: EstimateVesselReservation,
  ): VesselReservationFragment => {
    const existing = vesselReservationsById?.get(simplified.id);
    return {
      __typename: 'VesselReservation',
      ...simplified,
      shipByVesselId: existing?.shipByVesselId ?? null,
      locationByStartPortId: getLocationForFragment(simplified.startPortId),
      locationByEndPortId: getLocationForFragment(simplified.endPortId),
      regionByStartRegionId: getRegionForFragment(simplified.startRegionId),
      regionByEndRegionId: getRegionForFragment(simplified.endRegionId),
    };
  };

  return { toOpenPositionsFragment, toVesselReservationFragment };
}
